import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiAlertTriangle } from "react-icons/fi";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { getData, deleteData, postData } from "../../utils/api";

function formatDateTime(isoString) {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

const ManualPolling = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const historyPlatformFilter = (() => {
    const raw = (
      location.state?.platform ??
      searchParams.get("platform") ??
      ""
    )
      .toString()
      .toLowerCase();
    return raw === "amazon" || raw === "shopify" || raw === "flipkart"
      ? raw
      : null;
  })();
  const [history, setHistory] = useState({ rows: [], total: 0, page: 1, limit: 10, totalPages: 1 });
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const [deletingId, setDeletingId] = useState(null);
  const [syncingCheckpoint, setSyncingCheckpoint] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [showSyncPollModal, setShowSyncPollModal] = useState(false);
  const historyLimit = 10;
  const prevHistoryPlatformFilter = useRef(null);

  const executeSyncPollDates = async () => {
    setShowSyncPollModal(false);
    setSyncMessage(null);
    setSyncingCheckpoint(true);
    try {
      const response = await postData("/data/polling/sync-last-new-order-polled", {
        platform: "amazon",
      });
      setSyncMessage(response?.message || "Poll dates synced.");
    } catch (err) {
      setSyncMessage(
        err?.response?.data?.message || err?.message || "Failed to sync poll dates.",
      );
    } finally {
      setSyncingCheckpoint(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Remove this manual poll history record?")) return;
    setDeletingId(row.id);
    try {
      await deleteData(`/data/webhook/manual-poll-history/${row.id}`);
      await loadHistory(historyPage);
    } catch (err) {
      console.error("Failed to delete history", err);
      alert(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const loadHistory = useCallback(
    async (page = 1) => {
      setHistoryLoading(true);
      try {
        const qs = new URLSearchParams({
          page: String(page),
          limit: String(historyLimit),
        });
        if (historyPlatformFilter) {
          qs.set("platform", historyPlatformFilter);
        }
        const response = await getData(
          `/data/webhook/manual-poll-history?${qs.toString()}`,
        );
        if (response?.status && response?.data) {
          setHistory({
            rows: response.data.rows || [],
            total: response.data.total ?? 0,
            page: response.data.page ?? 1,
            limit: response.data.limit ?? historyLimit,
            totalPages: response.data.totalPages ?? 1,
          });
        }
      } catch (err) {
        console.error("Failed to load manual poll history", err);
        setHistory((prev) => ({ ...prev, rows: [] }));
      } finally {
        setHistoryLoading(false);
      }
    },
    [historyLimit, historyPlatformFilter],
  );

  useEffect(() => {
    const filterChanged =
      prevHistoryPlatformFilter.current !== null &&
      prevHistoryPlatformFilter.current !== historyPlatformFilter;
    prevHistoryPlatformFilter.current = historyPlatformFilter;
    if (filterChanged) {
      setHistoryPage(1);
      loadHistory(1);
      return;
    }
    loadHistory(historyPage);
  }, [historyPage, historyPlatformFilter, loadHistory]);

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <button
          type="button"
          onClick={() => navigate("/analytics/polling-status")}
          className="text-gray-600 hover:text-gray-900 flex items-center gap-1"
        >
          ← Back to Polling Status
        </button>
        <div className="flex flex-wrap gap-2">
          {(!historyPlatformFilter || historyPlatformFilter === "amazon") && (
            <>
              <button
                type="button"
                onClick={() =>
                  navigate("/analytics/manual-poll-by-date?platform=amazon")
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Run by date (Amazon)
              </button>
              <button
                type="button"
                onClick={() => setShowSyncPollModal(true)}
                disabled={syncingCheckpoint}
                className="px-4 py-2 border border-amber-600 text-amber-700 rounded-md hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {syncingCheckpoint ? "Syncing..." : "Sync poll dates"}
              </button>
            </>
          )}
          {(!historyPlatformFilter || historyPlatformFilter === "shopify") && (
            <button
              type="button"
              onClick={() =>
                navigate("/analytics/manual-poll-by-date?platform=shopify")
              }
              className="px-4 py-2 bg-emerald-700 text-white rounded-md hover:bg-emerald-800"
            >
              Run by date (Shopify)
            </button>
          )}
          {(!historyPlatformFilter || historyPlatformFilter === "flipkart") && (
            <button
              type="button"
              onClick={() =>
                navigate("/analytics/manual-poll-by-date?platform=flipkart")
              }
              className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700"
            >
              Run by date (Flipkart)
            </button>
          )}
        </div>
      </div>

      {syncMessage && (
        <div
          className={`mb-4 text-sm ${
            syncMessage.toLowerCase().includes("updated") ||
            syncMessage.toLowerCase().includes("synced") ||
            syncMessage.toLowerCase().includes("already")
              ? "text-green-600"
              : "text-red-600"
          }`}
        >
          {syncMessage}
        </div>
      )}

      {showSyncPollModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowSyncPollModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="sync-poll-dates-dialog-title"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 id="sync-poll-dates-dialog-title" className="text-lg font-semibold text-gray-900">
                Sync poll dates
              </h2>
              <button
                type="button"
                onClick={() => setShowSyncPollModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Set <span className="font-medium">Last New Order Polled</span> and{" "}
              <span className="font-medium">Last Order Status Update</span> from your latest
              successful manual poll.
            </p>

            <div className="flex gap-3 rounded-md border border-red-200 bg-red-50 p-4 mb-6">
              <FiAlertTriangle
                className="text-red-600 flex-shrink-0 mt-0.5"
                size={20}
                aria-hidden
              />
              <div className="text-sm text-red-800">
                <p className="font-semibold mb-1">Warning</p>
                <p>
                  Only do this if manual polling already covered every order through that date.
                  After sync, Amazon cron will skip older orders and will not backfill anything you missed.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowSyncPollModal(false)}
                disabled={syncingCheckpoint}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={executeSyncPollDates}
                disabled={syncingCheckpoint}
                className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {syncingCheckpoint ? "Syncing..." : "Sync poll dates"}
              </button>
            </div>
          </div>
        </div>
      )}

      <h2 className="text-lg font-semibold mb-1">
        Manual Poll History
        {historyPlatformFilter ? (
          <span className="text-base font-normal text-gray-600 dark:text-gray-400 normal-case">
            {" "}
            — {historyPlatformFilter}
          </span>
        ) : null}
      </h2>
      {historyPlatformFilter ? (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Showing runs for{" "}
          <span className="font-medium capitalize">{historyPlatformFilter}</span>{" "}
          only.{" "}
          <button
            type="button"
            className="text-blue-600 hover:underline font-medium"
            onClick={() => navigate("/analytics/manual-polling")}
          >
            View all platforms
          </button>
        </p>
      ) : (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          All platforms. Use Polling Status → Run by date to open this page for
          one platform only.
        </p>
      )}
      {historyLoading ? (
        <div className="text-gray-500 ">Loading history...</div>
      ) : history.rows.length === 0 ? (
        <div className="text-gray-500 ">
          {historyPlatformFilter
            ? `No manual poll history yet for ${historyPlatformFilter}.`
            : "No history yet."}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded border border-gray-200 ">
            <table className="min-w-full bg-white  text-sm">
              <thead>
                <tr className="border-b border-gray-200  bg-gray-50 ">
                  <th className="px-3 py-2 text-left font-medium text-gray-700 ">
                    Date from
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 ">
                    Date to
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 ">
                    Platform
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 ">
                    Total fetched
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 ">
                    Created
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 ">
                    Skipped
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 ">
                    Failed
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 ">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 ">
                    Triggered at
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 ">
                    Error
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 ">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {history.rows.map((row) => (
                  <tr
                    key={row.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/analytics/manual-poll-history/${row.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/analytics/manual-poll-history/${row.id}`);
                      }
                    }}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-3 py-2 text-gray-900  whitespace-nowrap">
                      {typeof row.dateFrom === "string" && row.dateFrom.length > 10
                        ? formatDateTime(row.dateFrom)
                        : row.dateFrom}
                    </td>
                    <td className="px-3 py-2 text-gray-900  whitespace-nowrap">
                      {typeof row.dateTo === "string" && row.dateTo.length > 10
                        ? formatDateTime(row.dateTo)
                        : row.dateTo}
                    </td>
                    <td className="px-3 py-2 capitalize text-gray-900 ">
                      {row.platform}
                    </td>
                    <td className="px-3 py-2 text-gray-900 ">
                      {row.totalOrdersFetched}
                    </td>
                    <td className="px-3 py-2 text-gray-900 ">
                      {row.createdCount}
                    </td>
                    <td className="px-3 py-2 text-gray-900 ">
                      {row.skippedCount}
                    </td>
                    <td className="px-3 py-2 text-gray-900 ">
                      {Array.isArray(row.failedOrderIds)
                        ? row.failedOrderIds.length
                        : 0}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          row.status === "success"
                            ? "text-green-600 "
                            : "text-red-600"
                        }
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600  whitespace-nowrap">
                      {formatDateTime(row.createdAt)}
                    </td>
                    <td
                      className="px-3 py-2 text-gray-600  max-w-xs truncate"
                      title={row.errorMessage || ""}
                    >
                      {row.errorMessage || "—"}
                    </td>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => handleDelete(row)}
                        disabled={deletingId === row.id}
                        className="px-2 py-1 bg-red-500 text-sm text-white hover:bg-red-300 rounded disabled:opacity-50"
                      >
                        {deletingId === row.id ? "Deleting..." : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
            <p className="text-sm text-gray-600 ">
              Total {history.total} record{history.total !== 1 ? "s" : ""}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                disabled={historyPage <= 1}
                className="px-3 py-1.5 border border-gray-300  rounded-md text-gray-700  hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600 ">
                Page {history.page} of {history.totalPages}
              </span>
              <button
                type="button"
                onClick={() =>
                  setHistoryPage((p) => Math.min(history.totalPages, p + 1))
                }
                disabled={historyPage >= history.totalPages}
                className="px-3 py-1.5 border border-gray-300  rounded-md text-gray-700  hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ManualPolling;
